<script lang="js">
import { defineComponent } from "vue";
import Paginator from "@/components/shared/PaginationComponent.vue";

export default defineComponent({
  name: "AppCustomTable",

  components: {Paginator },
  props: {
    tableName: {
      type: String,
      default: "Data",
    },
    headers: {
      type: Array,
      required: true,
    },
    items: {
      type: Array,
      default: () => [],
    },
    title: {
      type: String,
      default: "",
    },
    rowStyles: {
      type: [String, Function],
      default: "",
    },
    columnStyles: {
      type: [Object, Function],
      default: () => ({}),
    },
    dropdownOptions: {
      type: Array,
      default: () => ["Edit", "View", "Delete"],
    },
    showMenu: {
      type: Boolean,
      default: false,
    },
    showIcon: {
      type: Boolean,
      default: false,
    },
    height: {
      type: String,
      default: "auto",
    },
    showLegend: {
      type: Boolean,
      default: false,
    },
    columnWidths: {
      type: Object,
      default: () => ({}),
    },
    doubleHeader: {
      type: Boolean,
      default: false,
    },
    topHeaders: {
      type: Array,
      default: () => [],
    },
    showActionBtn: {
      type: Boolean,
      default: false,
    },
    disableActionBtn: {
      type: Boolean,
      default: false,
    },
    actionBtnText: {
      type: String,
      default: "",
    },
    actonBtnIcon: {
      type: String,
      default: "",
    },
    showPagination: {
      type: Boolean,
      default: false,
    },
    loadMorePaginationData: {
      type: Function,
      default: () => ({}),
    },
    isDeleteIcon: {
      type: Boolean,
      default: false,
    },
    hideLastIcon: {
      type: Boolean,
      default: false,
    },
    totalItems: {
      type: Number,
      default: 0,
    },
    itemsPerPage: {
      type: Number,
      default: 10,
    },
    currentPage: {
      type: Number,
      default: 1,
    },
    showTotal: {
      type: Boolean,
      default: false,
    },
    totals: {
      type: Object,
      default: () => ({}),
    },
    styles: {
      type: String,
      default: "",
    },
    headerStyles: {
      type: [String, Object],
      default: "",
    },
    menuStyles: {
      type: String,
      default: "",
    },
    scrollHorizontally: {
      type: Boolean,
      default: false,
    },
    scrollVertically: {
      type: Boolean,
      default: false,
    },
    showCurrency: {
      type: Boolean,
      default: true,
    },
    currencySymbol: {
      type: String,
      default: "€",
    },
    showCheckBox: {
      type: Boolean,
      default: false,
    },
    legends: {
      type: Array,
      default: () => ['Pending', 'Paid'],
    },
    legendStyles: {
      type: Object,
      default: () => ({})
    },
    draggableRows: {
      type: Boolean,
      default: false,
    },

    errorMessage: {
      type: String,
      default: ""
    },
    showErrorMessage: {
      type: Boolean,
      default: false
    },
    headerToolTips: {
      type: Object,
      default: () => ({})
    },
    sortable: {
      type: Boolean,
      default: false
    },
    fallbackMessage:{
      type: Object,
      default: () => ({
        title: "No Data Found",
        subtitle: "You will see all data here when you add one."
      })
    }
  },
  data() {
    return {
      hoveredRow: -1,
      currentPageRef: this.currentPage,
      show: null,
      countRowSelected: 0,
      multipleRowSelected: [],
      headerCheck: false,
      selectedItems: [],
      displayedItems: [],
      dragItemIndex: null,
      staticHeaderStyle: {
        display: "block",
        position: "fixed",
        top: "70px",
        width: "90.6%",
        "overflow-x": "auto"
      },
      tableIsAtTop: false,
      clonedTableHead: null,
      tooltipPosition: { left: 0, top: 0 },
      headerToShowTooltip: null,
      order_by: this.headers?.reduce((acc, curr) => {
        acc[curr.key] = false
        return acc
      }, {})
    };
  },
  emits: ["menu-click", "item-click", "is-checked"],
  computed: {
    isAllSelected() {
      return this.items.length > 0 && this.selectedItems.length === this.items.length;
    },
    isIndeterminate() {
      return this.selectedItems.length > 0 && this.selectedItems.length < this.items.length;
    },
    tooltipText() {
      return this.headerToShowTooltip ? this.headerToolTips[this.headerToShowTooltip] : ""
    },
    columnHeaders() {
      const headers = [...this.headers]

      const topHeaders = this.topHeaders.filter(header => header.rowspan)
      topHeaders.forEach(header => {
        headers.splice(header.position, 0, {key: header?.key, label: header?.title, type: header?.type})
      })

      return headers
    },
    getColumWidth() {
      return (header) => {
        return this.columnWidths[header.key] || "auto";
      };
    },
    statusBackgroundColor(){
      return (status) => {
        switch (status) {
          case "Paid":
          case "Approved":
          case "Signed":
            return "bg-[#ECFDF3] text-[#027A48] w-max";
          case "Pending":
          case "Not signed":
            return "bg-[#FFFAEB] text-[#B54708] w-max";
          case "Draft":
            return "bg-[#eef4ff] text-[#3538cd] w-max";
          case "Rejected":
            return "bg-red-100 text-red-500 w-max";
          default:
            return "";
        }
      }
    },

    columnSum() {
      return (columnKey) => {
        return this.items.reduce((acc, curr) => {
          if(curr?.exclude_from_total) return acc;
          const value = this.parseValue(curr[columnKey]);
          return acc + value;
        }, 0);
      }
    },
    getIcon() {
      return (key) => {
        return this.order_by[key] ? "mdi-arrow-down" : "mdi-arrow-up";
      };
    },


  },
  methods: {
    handleSort(key){
      this.order_by[key] = !this.order_by[key]
      const order = this.order_by[key] ? 2 : 1;
      this.$emit('sort', { key, order })

    },
    handleShowTooltip(event, header) {
      if(header.showTooltip) {
        const isLastColumn = this.columnHeaders[this.columnHeaders.length - 1].key === header.key;
        this.headerToShowTooltip = header?.key;
        this.tooltipPosition.left = event.clientX - (isLastColumn ? 300 : 100);
        this.tooltipPosition.top = (event.currentTarget.offsetTop + event.currentTarget.offsetHeight) - 5;
      }
    },
    drag(event, item, index) {
      this.dragItemIndex = index;
    },
    allowDrop(event) {

      event.preventDefault();
    },
    drop(event, item, index) {
      const dragItem = this.displayedItems[this.dragItemIndex];
      this.displayedItems.splice(this.dragItemIndex, 1);
      this.displayedItems.splice(index, 0, dragItem);
      this.dragItemIndex = null;
    },
    showList(item) {
      this.show = item;
    },
    hideList() {
      this.show = null;
    },
    showDeleteIcon(index) {
      this.hoveredRow = index;
    },
    hideDeleteIcon() {
      this.hoveredRow = -1;
    },
    calculateSum(columnIndex) {
      if (columnIndex) {
        return this.items.reduce((acc, curr) => {
          const value = this.parseValue(curr[this.headers[columnIndex].key]);
          return acc + value;
        }, 0);
      } else {
        return '';
      }
    },
    parseValue(value) {
      if(typeof value === 'number') {
        return value
      } else if(typeof value === 'string' && /^\d+$/.test(value)) {
        return parseFloat(value)
      } else {
        return 0
      }
    },

    checkboxClick(event) {
      event?.preventDefault();
    },
    handleSelectAllRow(event) {
      this.headerCheck = event.target.checked;
      this.selectedItems = [];

      if (event.target.checked) {
        this.items.forEach((item) => {
          if (item.id) {
            this.selectedItems.push(item.id);
          }
        });
      }

      this.$emit('is-checked', this.selectedItems);
    },
    handleRowSelect(rowId, event) {
      if (event.target.checked) {
        if (!this.selectedItems.includes(rowId)) {
          this.selectedItems.push(rowId);
        }
      } else {
        const index = this.selectedItems.indexOf(rowId);
        if (index > -1) {
          this.selectedItems.splice(index, 1);
        }
      }

      // Update header checkbox state
      this.headerCheck = this.isAllSelected;

      this.$emit('is-checked', this.selectedItems);
    },
    // remove duplicate elements from an array
    removeDuplicates(arr) {
      return Array.from(new Set(arr));
    },
    formatCurrency(amount) {
      return this.parseValue(amount) === 0 ? '-' : this.parseValue(amount)?.germanCurrencyFormatter();
    },
  },
  mounted() {
    this.displayedItems = this.items;


  },

  watch: {
    currentPage(newVal) {
      this.currentPageRef = newVal
    },
    items(newVal) {
      this.displayedItems = newVal;
    },

  }
});
</script>

<template>
  <section
    class="w-full relative !z-10 border-2 border-gray-100 rounded-lg bg-white mb-10"
    :style="{ height: height }"
    :id="`table-${tableName}`"
  >
    <article
      v-if="title"
      class="flex items-center justify-between w-full px-6 py-4"
    >
      <header class="font-medium text-md text-[#101828]">
        <h4></h4>
        <h4>
          {{ $t(title.getTranslationKey()) || title }}
          <span v-if="showCheckBox && selectedItems?.length"
          >({{ selectedItems.length }}
            {{ $t("Selected".getTranslationKey()) }})</span
          >
        </h4>
      </header>
      <legend class="flex items-center gap-4">
        <slot name="legends">
          <div class="flex gap-1" v-if="showLegend">
            <div
              v-for="legend in legends"
              :key="legend"
              class="flex items-center gap-2 px-3 text-sm text-center rounded-full"
              :class="legendStyles[legend] || statusBackgroundColor(legend)"
            >
              <div
                class="w-2 h-2 rounded-full"
                :class="{
                'bg-[#12B76A]': legend === 'Paid' || legend === 'Approved',
                'bg-[#F79009]': legend === 'Pending',
                'bg-[#3538CD]': legend === 'Draft',
                'bg-yellow-100': legend === 'Clarification',
              }"
              ></div>
              {{ $t(legend.getTranslationKey()) }}
            </div>
          </div>
        </slot>
        <div>
          <slot name="action-btn">
            <app-primary-button
              :buttonLabel="$t(actionBtnText.getTranslationKey())"
              data-cy="btn_add"
              @click="$emit('menu-click', { option: 'add' })"
              v-if="showActionBtn"
              class=""
              :disabled="disableActionBtn"
            >
              <template #icon>
                <v-icon
                  :icon="actonBtnIcon ? actonBtnIcon : 'mdi-plus'"
                ></v-icon>
              </template>
            </app-primary-button>
          </slot>
        </div>
      </legend>
    </article>
    <article
      :class="[
        'w-full',
        scrollHorizontally ? 'overflow-x-auto' : 'overflow-x-hidden',
        scrollVertically && items?.length > 10 ? 'overflow-y-auto h-[600px]' : 'overflow-y-hidden',
      ]"
    >
      <table
        :class="[
          'w-full text-sm text-left text-[#404b5e] font-[inter]',
          styles,
        ]"
        :ref="`table-${tableName}`"
        aria-label="custom table"
      >
        <thead>
        <tr v-if="doubleHeader">
          <th
            scope="col"
            v-for="(headerItem, index) in topHeaders"
            :key="headerItem.title"
            :class="[
                'px-6 py-3 !font-medium border-r text-center',
                { 'border-b': !headerItem?.rowspan, 'top-0': scrollVertically },
                typeof headerStyles === 'object'
                  ? headerStyles[headerItem.key]
                  : headerStyles,
              ]"
            :style="{ width: getColumWidth({ key: headerItem?.key || '' }) }"
            :colspan="
                headerItem.colspan
                  ? headerItem.colspan
                  : index === topHeaders.length - 1
                  ? 2
                  : 1
              "
            :rowspan="headerItem?.rowspan"
            @mouseenter="handleShowTooltip($event, headerItem)"
            @mouseleave="headerToShowTooltip = null"
          >
            <slot
              :name="`header-${headerItem.key?.toLowerCase()}`"
              :item="headerItem.title"
            >
              <div
                :class="[
                    headerItem.type === 'currency' || headerItem.type === 'digit'
                      ? 'flex justify-end'
                      : 'flex items-center',
                  ]"
              >
                <div
                  :style="{
                      width: getColumWidth({ key: headerItem?.key || '' }),
                    }"
                >
                  {{
                    headerItem?.noTranslation
                      ? headerItem.title
                      : $t(headerItem.title.getTranslationKey())
                  }}
                </div>
                <div
                  class="pl-1"
                  v-if="headerItem.type === 'currency' && showCurrency"
                >
                  ({{ currencySymbol }})
                </div>
              </div>
            </slot>
          </th>
        </tr>
        <tr>
          <th
            v-if="showCheckBox && items?.length"
            :class="['px-4', {'top-0': scrollVertically}]"
            scope="col"
            style="width: 5px"
          >
            <input
              type="checkbox"
              class="cursor-pointer"
              :checked="isAllSelected"
              :indeterminate="isIndeterminate"
              @change="handleSelectAllRow"
              @click.stop
            />
          </th>

          <th
            scope="col"
            v-for="header in headers"
            :key="header.key"
            :style="{ width: getColumWidth(header) }"
            :class="[
                doubleHeader ? 'top-[44px] border-r' : 'top-0',
                'px-6  py-3 !font-medium',
                typeof headerStyles === 'object'
                  ? headerStyles[header.key]
                  : headerStyles,
              ]"
            @mouseenter="handleShowTooltip($event, header)"
            @mouseleave="headerToShowTooltip = null"
          >
            <slot
              :name="`header-${header.key?.toLowerCase()}`"
              :item="header.label"
            >
              <div
                :class="[
                    header.type === 'currency' || header.type === 'digit'
                      ? 'flex justify-end'
                      : 'flex items-center',
                  ]"
              >
                <div :style="{ width: getColumWidth(header) }">
                  {{
                    header?.noTranslation
                      ? header.label
                      : $t(header.label?.getTranslationKey())
                  }}

                </div>

                <div
                  class="pl-1"
                  v-if="header.type === 'currency' && showCurrency"
                >
                  ({{ currencySymbol }})
                </div>
                <div v-if="sortable">
                  <v-icon
                    class="cursor-pointer hover:text-[#8B2923]"
                    size="16"
                    :icon="getIcon(header.key)"
                    @click="handleSort(header.key)"
                  ></v-icon>
                </div>
              </div>
            </slot>
          </th>
          <th id="menu" v-if="isDeleteIcon || showMenu || showIcon" :class="[{'top-0': scrollVertically}]"></th>
        </tr>
        </thead>
        <tbody>
        <tr
          v-for="(item, index) in displayedItems"
          :key="index"
          :class="[
              typeof rowStyles === 'function'
                ? rowStyles(item, index)
                : rowStyles,
              'relative w-full',
            ]"
          @click="$emit('item-click', { ...item, option: 'View', index })"
          @mouseover="showDeleteIcon(index)"
          :draggable="draggableRows"
          @dragstart="drag($event, item, index)"
          @dragover="allowDrop($event)"
          @drop="drop($event, item, index)"
        >
          <!-- checkbox -->
          <td v-if="showCheckBox" class="px-4">
            <input
              type="checkbox"
              class="cursor-pointer"
              :checked="selectedItems.includes(item.id)"
              @change="handleRowSelect(item.id, $event)"
              @click.stop
            />
          </td>
          <td
            v-for="header in columnHeaders"
            :key="header.key"
            :class="[
                (typeof columnStyles === 'function'
                  ? columnStyles(header.key, item[header.key], item, index)
                  : columnStyles[header.key]) || '',
                'px-4 py-3 ',
                header.type === 'digit' || header.type === 'currency'
                  ? 'text-right'
                  : 'text-left w-max',
              ]"
          >
            <div >
              <slot :name="header.key" :item="{ ...item, index }">
                <p
                  :class="[
                      header.key === 'status' &&
                        statusBackgroundColor(item.status_action),
                      'px-3 rounded-full',
                    ]"
                  v-if="header.type === 'currency'"
                >
                  {{
                    item.isHeading || item.is_header
                      ? ""
                      : formatCurrency(item[header.key]) || "-"
                  }}
                </p>
                <p
                  :class="[
                      header.key === 'status' &&
                        statusBackgroundColor(item.status_action),
                      'px-3 rounded-full',
                    ]"
                  v-else-if="header.type === 'date'"
                >
                  {{ item[header.key]?.formattedDate() || "-" }}
                </p>
                <p
                  :class="[
                      header.key === 'status' &&
                        statusBackgroundColor(item.status_action),
                      'px-3 rounded-full flex',
                    ]"
                  v-else-if="header.type === 'list'"
                >
                  {{
                    item.isHeading || item.is_header || item.isTotal
                      ? ""
                      : item[header.key]?.length
                        ? item[header.key][0]
                        : "-"
                  }}
                  <span
                    class="count"
                    v-if="item[header.key]?.length > 1"
                    @mouseenter="showList(item[header.key])"
                    @mouseleave="hideList()"
                  >
                      +{{ item[header.key]?.length - 1 }}
                    </span>
                </p>
                <p
                  :class="[
                      header.key === 'status' &&
                        statusBackgroundColor(item.status_action),
                      item.isHeading || item.is_header ? 'font-bold' : '',
                      'px-3 rounded-full',
                    ]"
                  v-else
                >
                  {{
                    item[header.key]
                      ? item[header.key]
                      : item.isHeading || item.is_header
                        ? ""
                        : "-"
                  }}
                </p>
                <template v-if="header.type === 'list'">
                  <div
                    :class="[
                        { 'modal countries-modal': header.type === 'list' },
                        ' px-3 rounded-full',
                      ]"
                    v-if="show === item[header.key]"
                  >
                    <p
                      v-for="list in item[header.key]"
                      :key="list"
                      class="cursor-pointer"
                    >
                        <span v-if="item[header.key][0] !== list">
                          {{ list }}
                        </span>
                    </p>
                  </div>
                </template>
              </slot>
            </div>
          </td>
          <td :class="['z-10 mt-3', menuStyles]" v-if="showMenu">
            <slot name="menu-item" :item="{ ...item, index }">
              <v-menu location="start">
                <template #activator="{ props }">
                  <v-icon
                    icon="custom:dotVertical"
                    v-bind="props"
                    class="mt-2 mr-3 text-center cursor-pointer"
                  ></v-icon>
                </template>
                <v-list class="rounded-lg item">
                  <slot name="listItems" :item="item">
                    <v-list-item
                      v-for="option in dropdownOptions"
                      class="hover:bg-gray-200 !text-[#344054] text-sm cursor-pointer !min-h-0 "
                      :key="option"
                      @click="$emit('menu-click', { ...item, option, index })"
                    >{{ $t(option.getTranslationKey()) }}</v-list-item
                    >
                  </slot>
                </v-list>
              </v-menu>
            </slot>
          </td>
          <td v-if="isDeleteIcon" class="w-[3%]">
            <v-icon
              v-if="!(hideLastIcon && index === items.length - 1)"
              icon="custom:trashIcon"
              class="ml-auto cursor-pointer"
              @click="$emit('menu-click', { option: 'delete', item, index })"
            ></v-icon>
          </td>
          <td class="" v-if="showIcon">
            <v-icon
              v-show="hoveredRow === index"
              icon="custom:trashIcon"
              class="cursor-pointer"
              @click="$emit('icon-click', { option: 'Delete', item, index })"
            ></v-icon>
          </td>
        </tr>
        <tr
          v-if="showTotal"
          :class="[
                  'font-bold',
                  typeof rowStyles === 'function'
                ? rowStyles(null, displayedItems.length)
                : rowStyles,
                  ]">
          <td
            v-for="(item, index) in columnHeaders"
            :key="item.key"
            :class="[
                  'px-6 py-3 totalColumn',
                  (typeof columnStyles === 'function'
                  ? columnStyles('total_row', null, item, index)
                  : columnStyles?.total_row) || '',
                  ]"
          >
              <span v-if="index === 0"
              ><slot name="total_title">{{
                  $t("Total".getTranslationKey())
                }}</slot></span
              >
            <div v-else-if="item.type === 'currency'">
              <div class="flex justify-end gap-1" v-if="columnSum(item.key)">
                <span>{{ currencySymbol + " " }}</span>
                <span>{{
                    totals[item.key]
                      ? totals[item.key]?.germanCurrencyFormatter()
                      : columnSum(item.key)?.germanCurrencyFormatter()
                  }}</span>
              </div>
              <p class="text-right" v-else>-</p>
              <!--                {{ columnSum(item.key) ? currencySymbol + " " + columnSum(item.key)?.germanCurrencyFormatter() : '-'}}-->
            </div>
          </td>
          <th id="additionalHeader" v-if="isDeleteIcon || showMenu || showIcon"></th>
        </tr>
        </tbody>
      </table>
    </article>
    <!--    Header tooltip-->
    <div
      v-if="headerToShowTooltip"
      class="absolute z-50 bg-white text-xs !text-gray-700 px-3 py-1 shadow-md rounded-lg w-max max-w-[200px] text-wrap"
      :style="{
        left: tooltipPosition.left + 'px',
        top: tooltipPosition.top + 'px',
      }"
    >
      {{ $t(tooltipText.getTranslationKey()) }}
    </div>
    <!--    Header tooltip-->
    <div class="empty_state text-sm text-[#101828]" v-if="!items || items?.length <= 0">
      <v-icon icon="custom:NewEmptyIcon" size="200"></v-icon>
      <h3 class="capitalize font-medium ">
        {{ $t(fallbackMessage.title.getTranslationKey()) }}
      </h3>

      <p class="font-normal " >
        {{ $t(fallbackMessage.subtitle.getTranslationKey()) }}
      </p>
    </div>
    <footer>
      <slot name="table-footer">
        <div
          class="mt-3 ml-auto mr-6 w-max"
          v-if="showPagination && totalItems > itemsPerPage"
        >
          <Paginator
            :total-items="totalItems"
            v-model="currentPageRef"
            :items-per-page="itemsPerPage"
            :on-click="loadMorePaginationData"
          />
        </div>
      </slot>
    </footer>
  </section>
</template>

<style scoped>
.modal,
.countries-modal {
  position: absolute;
  z-index: 30;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: space-between;
  padding: 20px;
  width: auto;
  height: auto;
  background: #ffffff;
  border: 1px solid #f2f4f7;
  box-shadow: 0 12px 16px -4px rgba(16, 24, 40, 0.08),
  0 4px 6px -2px rgba(16, 24, 40, 0.03);
  border-radius: 8px;
}

.modal {
  padding: 20px;
}

.countries-modal {
  left: auto;
  padding: 10px;
}
.count {
  font-size: small;
  margin-left: 5px;
  padding: 2px 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 22px;
  background: #f2f4f7;
  border-radius: 16px;
  width: max-content;
}

.empty_state {
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  width: 100% !important;
  padding: 20px;
  flex-direction: column;
  min-height: 400px;
  margin: auto;
}

input[type="checkbox"] {
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;
  width: 20px;
  height: 20px;

  border: 1px solid #d0d5dd;
  border-radius: 6px;
  outline: none;
  transition: all 0.3s;
  position: relative;
}

input[type="checkbox"]:checked {
  background: #f4eae9;
  border: 1px solid #ae6a65;
}

input[type="checkbox"]:checked::before {
  content: "✔";
  color: #ae6a65;
  font-size: 12px;
  font-weight: bold;
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
}

.selected-row {
  background: #fbf7f6 !important;
}
tbody tr:nth-child(even) {
  background: #f9fafb;
}

thead th {
  background: #f2f4f7;
  position: sticky;
  z-index: 10;
}

tr > th {
  background-color: #f2f4f7;
  border-right: 1px solid #eaecf0;
}

tr > td {
  border-right: 1px solid #eaecf0;
}

th:nth-last-child(1), td:nth-last-child(1) {
  border-right: none;
}

tr {
  border-bottom: 1px solid #eaecf0;
}

tr:nth-last-child(1) {
  border-bottom: none;
}

::-webkit-scrollbar {
  width: 5px;
  height: 5px;
}

::-webkit-scrollbar-thumb:hover {
  cursor: pointer;
}

::-webkit-scrollbar-thumb {
  background: #888;
  border-radius: 5px;
}

::-webkit-scrollbar-thumb:hover {
  background: #555;
}

</style>
